import {
  useSuspenseInfiniteQuery,
  type InfiniteData,
} from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import { customerOrdersInfiniteQueryOptions } from "@modules/account/account.queryOptions";
import type { CustomerOrdersQuery } from "@gql/customer/types";

export const Route = createFileRoute(
  "/$locale/_layout/(protected)/_protected/account/orders/",
)({
  component: OrdersPage,
  loader: ({ context }) =>
    context.queryClient.ensureInfiniteQueryData(
      customerOrdersInfiniteQueryOptions(),
    ),
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    return {
      meta: [
        ...seo({
          title: t("Seo:orders.title"),
          description: t("Seo:orders.description"),
        }),
      ],
    };
  },
});

type OrderNode = NonNullable<
  NonNullable<
    NonNullable<CustomerOrdersQuery["customer"]>["orders"]
  >["edges"][number]["node"]
>;

function formatMoney(
  amount: string | number,
  currency: string,
  locale: string,
) {
  const value = typeof amount === "string" ? parseFloat(amount) : amount;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

function formatDate(iso: string, locale: string) {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function OrdersPage() {
  const { t } = useTranslation();
  const { locale } = Route.useParams();

  const query = useSuspenseInfiniteQuery(customerOrdersInfiniteQueryOptions());
  const data = query.data as InfiniteData<CustomerOrdersQuery>;

  const orders: OrderNode[] = data.pages.flatMap(
    (page) =>
      page.customer?.orders?.edges
        ?.map((edge) => edge.node)
        .filter((node): node is OrderNode => Boolean(node)) ?? [],
  );

  return (
    <div className="min-h-screen px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Back link */}
        <Link
          to="/$locale/account"
          params={{ locale }}
          className="text-text-3 hover:text-primary text-small mb-4 inline-block"
        >
          ← {t("Account.backToAccount")}
        </Link>

        <h1 className="text-2xlarge text-text-1 mb-8 font-bold">
          {t("Account.orders.title")}
        </h1>

        {orders.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-text-3 mb-4">{t("Account.orders.empty")}</p>
            <Link
              to="/$locale"
              params={{ locale }}
              className="bg-primary text-white hover:bg-primary/90 inline-block rounded-lg px-6 py-3"
            >
              {t("Account.orders.startShopping")}
            </Link>
          </div>
        ) : (
          <>
            <ul className="space-y-4">
              {orders.map((order) => {
                const itemCount =
                  order.lineItems?.nodes?.reduce(
                    (sum, li) => sum + (li?.quantity ?? 0),
                    0,
                  ) ?? 0;

                return (
                  <li
                    key={order.id}
                    className="border-border-5 rounded-lg border p-6"
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <p className="text-text-1 font-semibold">
                          {t("Account.orders.orderNumber")}: {order.name}
                        </p>
                        <p className="text-text-3 text-small">
                          {order.processedAt
                            ? formatDate(order.processedAt, locale)
                            : ""}
                        </p>
                      </div>
                      {order.financialStatus ? (
                        <span className="bg-primary/10 text-primary text-small rounded-full px-3 py-1 font-medium">
                          {t(
                            `Account.orders.financialStatuses.${order.financialStatus}`,
                          )}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-text-3">
                        {t("Account.orders.items", { count: itemCount })}
                      </p>
                      {order.totalPrice ? (
                        <p className="text-text-1 font-semibold">
                          {formatMoney(
                            order.totalPrice.amount,
                            order.totalPrice.currencyCode,
                            locale,
                          )}
                        </p>
                      ) : null}
                    </div>
                    <Link
                      to="/$locale/account/orders/$orderId"
                      params={{
                        locale,
                        orderId: order.id,
                      }}
                      className="text-primary hover:text-primary/80 mt-4 inline-block text-sm"
                    >
                      {t("Account.orders.viewDetails")} →
                    </Link>
                  </li>
                );
              })}
            </ul>

            {query.hasNextPage ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                  className="border-border-5 text-text-1 hover:bg-background-2 disabled:opacity-50 rounded-lg border px-6 py-3"
                >
                  {query.isFetchingNextPage
                    ? t("Account.orders.loading")
                    : t("Account.orders.loadMore")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
